import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';

const BUSINESS_MODULES = [
  { value: 'dairy', label: 'Dairy Farm' },
  { value: 'chemicals', label: 'Chemicals' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'adhesive', label: 'Adhesive Tapes' },
];

const getModuleLabel = (module) => {
  if (!module || module === 'all') return 'All Modules';
  const found = BUSINESS_MODULES.find(m => m.value === module);
  return found ? found.label : module;
};

const getModuleStyle = (module) => {
  const colors = {
    dairy: { background: '#e3f2fd', color: '#1565c0' },
    chemicals: { background: '#ffebee', color: '#c62828' },
    agriculture: { background: '#e8f5e9', color: '#2e7d32' },
    adhesive: { background: '#fff3e0', color: '#e65100' },
    all: { background: '#ede7f6', color: '#4527a0' },
  };
  const c = colors[module] || colors.all;
  return {
    ...c,
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  };
};

const UsersManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'operator',
    business_module: 'dairy',
    new_password: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role,
        email: `${formData.username}@sohaibenterprises.cloud`,
        business_module: formData.role === 'businessman' ? 'all' : formData.business_module,
      };
      await userService.createUser(payload);
      alert('✅ User created successfully!');
      setShowAddModal(false);
      setFormData({ username: '', password: '', full_name: '', role: 'operator', business_module: 'dairy', new_password: '' });
      fetchUsers();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to create user'));
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        username: formData.username,
        full_name: formData.full_name,
        role: formData.role,
        business_module: formData.role === 'businessman' ? 'all' : formData.business_module,
      };

      await userService.updateUser(selectedUser.id, updateData);

      if (formData.new_password && formData.new_password.trim() !== '') {
        await userService.resetPassword(selectedUser.id, formData.new_password);
      }

      alert('✅ User updated successfully!');
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({ username: '', password: '', full_name: '', role: 'operator', business_module: 'dairy', new_password: '' });
      fetchUsers();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to update user'));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`⚠️ Are you sure you want to PERMANENTLY DELETE "${userName}"?\n\nThis action cannot be undone.`)) return;

    try {
      await userService.deleteUser(userId);
      alert('✅ User deleted successfully!');
      fetchUsers();
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to delete user'));
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      business_module: user.business_module || 'dairy',
      new_password: '',
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <h2>Loading users...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loading}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#e74c3c' }}>Error</h2>
        <p>{error}</p>
        <button onClick={fetchUsers} style={styles.retryButton}>🔄 Retry</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 Users Management</h1>
          <p style={styles.subtitle}>Manage system users and permissions</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
            ← Back to Dashboard
          </button>
          <button onClick={() => setShowAddModal(true)} style={styles.addButton}>
            ➕ Add New User
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Full Name</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Business Access</th>
              <th style={styles.th}>Created</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="7" style={styles.noData}>No users found</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} style={styles.row}>
                  <td style={styles.td}><strong>{user.username}</strong></td>
                  <td style={styles.td}>{user.full_name}</td>
                  <td style={styles.td}>
                    <span style={getRoleStyle(user.role)}>{user.role}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={getStatusStyle(user.is_active)}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={getModuleStyle(user.business_module)}>
                      {getModuleLabel(user.business_module)}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(user.created_at)}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={() => openEditModal(user)} style={styles.editButton}>
                        ✏️ Edit
                      </button>
                      <button onClick={() => handleDeleteUser(user.id, user.username)} style={styles.deleteButton}>
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add New User</h2>
            <form onSubmit={handleAddUser}>
              <input
                type="text"
                placeholder="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                style={styles.input}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                style={styles.input}
                required
              />
              <input
                type="text"
                placeholder="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                style={styles.input}
                required
              />
              <select
                value={formData.role}
                onChange={(e) => setFormData({
                  ...formData,
                  role: e.target.value,
                  business_module: e.target.value === 'businessman' ? 'all' : 'dairy'
                })}
                style={styles.input}
                required
              >
                <option value="operator">Operator (Employee)</option>
                <option value="businessman">Businessman (Owner)</option>
              </select>

              {formData.role === 'operator' && (
                <select
                  value={formData.business_module}
                  onChange={(e) => setFormData({ ...formData, business_module: e.target.value })}
                  style={styles.input}
                  required
                >
                  {BUSINESS_MODULES.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              )}

              {formData.role === 'businessman' && (
                <div style={{ padding: '10px 12px', background: '#ede7f6', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#4527a0', fontWeight: '600' }}>
                  👑 Businessman gets access to all modules
                </div>
              )}

              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowAddModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit User</h2>
            <form onSubmit={handleEditUser}>
              <input
                type="text"
                placeholder="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                style={styles.input}
                required
              />
              <input
                type="text"
                placeholder="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                style={styles.input}
                required
              />
              <select
                value={formData.role}
                onChange={(e) => setFormData({
                  ...formData,
                  role: e.target.value,
                  business_module: e.target.value === 'businessman' ? 'all' : formData.business_module
                })}
                style={styles.input}
                required
              >
                <option value="operator">Operator (Employee)</option>
                <option value="businessman">Businessman (Owner)</option>
              </select>

              {formData.role === 'operator' && (
                <select
                  value={formData.business_module}
                  onChange={(e) => setFormData({ ...formData, business_module: e.target.value })}
                  style={styles.input}
                  required
                >
                  {BUSINESS_MODULES.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              )}

              {formData.role === 'businessman' && (
                <div style={{ padding: '10px 12px', background: '#ede7f6', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#4527a0', fontWeight: '600' }}>
                  👑 Businessman gets access to all modules
                </div>
              )}

              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e0e0e0' }}>
                <h4 style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '12px' }}>
                  Reset Password (Optional)
                </h4>
                <input
                  type="password"
                  placeholder="New Password (leave blank to keep current)"
                  value={formData.new_password || ''}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  style={styles.input}
                />
              </div>

              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowEditModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getRoleStyle = (role) => {
  return role === 'businessman'
    ? { background: '#667eea', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }
    : { background: '#48bb78', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' };
};

const getStatusStyle = (isActive) => {
  return isActive
    ? { background: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }
    : { background: '#f8d7da', color: '#721c24', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' };
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f7fa',
    padding: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#7f8c8d',
    fontSize: '14px',
    margin: 0,
  },
  backButton: {
    padding: '10px 20px',
    background: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  addButton: {
    padding: '10px 20px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  tableContainer: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    background: '#f8f9fa',
  },
  th: {
    padding: '16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '700',
    color: '#2c3e50',
    borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: '#2c3e50',
  },
  noData: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#95a5a6',
    fontSize: '16px',
  },
  editButton: {
    padding: '6px 12px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
  },
  deleteButton: {
    padding: '6px 12px',
    background: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
  },
  loading: {
    padding: '60px 20px',
    textAlign: 'center',
  },
  retryButton: {
    marginTop: '20px',
    padding: '12px 24px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#2c3e50',
  },
  input: {
    width: '100%',
    padding: '12px',
    marginBottom: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    background: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
};

export default UsersManagement;